import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMutationMembershipCancelByMembershipIdAsMember,
  useQueryMemberGetCurrentMembershipSubscriptionAsMember,
  useQueryUserFormRulesAsPublic,
} from "@gymflow/api";
import { cn, pluralize } from "@gymflow/helpers";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { FormikProvider, useFormik } from "formik";
import { DateTime } from "luxon";
import { useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes } from "../../../hooks";
import { RouteFeature } from "../../../routes";
import {
  Button,
  ClockIcon,
  InfoBlock,
  Spinner,
  WarningSection,
} from "../../atoms";
import { FileShield02Icon } from "../../atoms/icons/FileShield02Icon";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import { FormCancelDateAsMemberSelect } from "./components/FormCancelDateAsMemberSelect";
import { FormCancellationReasonAsMemberSelect } from "./components/FormCancellationReasonAsMemberSelect";
import {
  cancelMembershipFormSchema,
  INITIAL_VALUES_CANCEL_MEMBERSHIP_FORM,
} from "./constants";
import { CancelMembershipFormValues } from "./types";

export const HostedPagesProfileCancelMembership = () => {
  const { t } = useTranslation();
  const { isEmbed } = usePortalRoutes();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const [parent] = useAutoAnimate();
  const { api } = useGymflowModels();
  const { timezone } = useClubSettings();
  const history = useHistory();
  const { notifyDanger, toast } = useContext(ToastContext);

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const { data: rules } = useQueryUserFormRulesAsPublic({ api });

  const minimumPeriodNotice = useMemo(() => {
    if (!rules) {
      return 0;
    }
    return (
      rules.find((rule) => rule.ruleType === "SUBSCRIPTION_CANCELLATION")
        ?.subscriptionCancellationRule?.minimumPeriodNotice ?? 0
    );
  }, [rules]);

  const {
    data: activeMembershipSubscription,
    isLoading: isLoadingActiveMembershipSubscription,
  } = useQueryMemberGetCurrentMembershipSubscriptionAsMember(
    {
      api,
    },
    { refetchOnWindowFocus: true },
  );

  const { mutateAsync: mutateAsyncMembershipCancelByMembershipId } =
    useMutationMembershipCancelByMembershipIdAsMember({ api });

  const formik = useFormik<CancelMembershipFormValues>({
    initialValues: INITIAL_VALUES_CANCEL_MEMBERSHIP_FORM,
    validationSchema: toFormikValidationSchema(cancelMembershipFormSchema),
    onSubmit: async ({ date, cancellationReason }) => {
      if (!date || !activeMembershipSubscription || !cancellationReason) {
        return;
      }
      try {
        await mutateAsyncMembershipCancelByMembershipId({
          membershipId: activeMembershipSubscription?.id,
          cancellationDate: date.value,
          cancellationReasonId: cancellationReason.value,
        });
        toast({
          message: t(
            "page.hostedPagesProfileCancelMembership.toast.cancellingScheduled",
          ),
          intent: "success",
          type: "small",
        });
        history.replace(createSiteOrEmbedLink(RouteFeature.SiteProfile));
      } catch (e) {
        if (isAxiosError(e)) {
          notifyDanger(e);
        }
      }
    },
  });

  const cancellationDate = formik.values.date?.value;

  const finalPaymentDate = useMemo(() => {
    if (!cancellationDate) {
      return undefined;
    }
    return DateTime.fromISO(cancellationDate)
      .setZone(timezone)
      .plus({ second: 1 })
      .minus({
        [activeMembershipSubscription?.billingType === "MONTHLY"
          ? "months"
          : "weeks"]: activeMembershipSubscription?.billingPeriod,
      });
  }, [
    activeMembershipSubscription?.billingPeriod,
    activeMembershipSubscription?.billingType,
    cancellationDate,
    timezone,
  ]);

  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <HostedPagesBackHeader
        label={t("page.hostedPagesProfileCancelMembership.header")}
      />
      <div
        ref={parent}
        className={cn(
          "flex flex-col items-center bg-gray-50 dark:bg-gray-900 px-4 py-6",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
      >
        {isLoadingActiveMembershipSubscription ? (
          <Spinner />
        ) : (
          <div className="flex w-full max-w-lg flex-col gap-10">
            <FormikProvider value={formik}>
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-3">
                  {activeMembershipSubscription?.endContractDate && (
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1.5">
                        <FileShield02Icon
                          className="size-5"
                          pathClassName="stroke-gray-500 dark:stroke-gray-400"
                        />
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t(
                            "page.hostedPagesProfileCancelMembership.contractEndDate",
                          )}
                        </div>
                      </div>
                      <div className="dark:text-gray-25 text-base font-semibold text-gray-950">
                        {DateTime.fromISO(
                          activeMembershipSubscription.endContractDate,
                        )
                          .setZone(timezone)
                          .toLocaleString(DateTime.DATE_MED)}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1.5">
                      <ClockIcon
                        className="size-5"
                        pathClassName="stroke-gray-500 dark:stroke-gray-400 stroke-1"
                      />
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t(
                          "page.hostedPagesProfileCancelMembership.cancellationNoticePeriod",
                        )}
                      </div>
                    </div>
                    <div className="dark:text-gray-25 text-base font-semibold text-gray-950">
                      {minimumPeriodNotice === 0
                        ? t(
                            "page.hostedPagesProfileCancelMembership.noNoticePeriod",
                          )
                        : pluralize(
                            t("page.hostedPagesProfileCancelMembership.month", {
                              minimumPeriodNotice,
                            }),
                            t(
                              "page.hostedPagesProfileCancelMembership.months",
                              {
                                minimumPeriodNotice,
                              },
                            ),
                            minimumPeriodNotice,
                          )}
                    </div>
                  </div>
                </div>
                <div className="h-px w-full bg-gray-300 dark:bg-gray-700" />
                <div className="flex flex-col gap-3">
                  {activeMembershipSubscription && (
                    <>
                      <FormCancellationReasonAsMemberSelect />
                      <FormCancelDateAsMemberSelect
                        activeMembership={activeMembershipSubscription}
                      />
                    </>
                  )}
                  {cancellationDate && (
                    <InfoBlock
                      text={t(
                        "page.hostedPagesProfileCancelMembership.infoBlock.willHaveAccess",
                        {
                          cancelDate: DateTime.fromISO(cancellationDate)
                            .setZone(timezone)
                            .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
                        },
                      )}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-6">
                {finalPaymentDate && finalPaymentDate > DateTime.now() && (
                  <WarningSection
                    label={t(
                      "page.hostedPagesProfileCancelMembership.warningSection.finalPayment.label",
                    )}
                    subLabel={
                      <Trans
                        i18nKey={
                          "page.hostedPagesProfileCancelMembership.warningSection.finalPayment.subLabel"
                        }
                        values={{
                          finalPaymentDate: finalPaymentDate.toLocaleString(
                            DateTime.DATE_MED_WITH_WEEKDAY,
                          ),
                        }}
                      />
                    }
                  />
                )}
                <div className="flex gap-4">
                  <Button
                    intent="default"
                    className="w-full"
                    onClick={() => history.goBack()}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    intent="danger"
                    className="w-full"
                    onClick={async () => await formik.submitForm()}
                  >
                    {t("common.confirm")}
                  </Button>
                </div>
              </div>
            </FormikProvider>
          </div>
        )}
      </div>
    </div>
  );
};
